import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import DatePicker from 'components/date-picker/date-picker';
import NumberField from 'components/number-field';
import { format, isValid, parseISO } from 'date-fns';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from 'utils/helpers';

import { LineItemVariant } from '../use-line-item-variant';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) => ({
  row: { padding: theme.spacing(1) },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
  },
  label: {
    color: theme.palette.tab.offselected,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    // Touch devices have no hover tooltip: let the label wrap instead of truncating.
    [COARSE_POINTER]: {
      whiteSpace: 'normal',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
  },
  labelWrapper: {
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  actionButton: {
    padding: theme.spacing(0.25),
    [COARSE_POINTER]: {
      width: theme.spacing(5.5),
      height: theme.spacing(5.5),
    },
  },
  actionButtonTouch: {
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
  },
  numeric: {
    '& input': {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
  },
  compact: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  compactHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  compactFields: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
  },
  compactField: {
    flex: 1,
    minWidth: 0,
  },
  card: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  cardTotal: {
    backgroundColor: 'transparent',
    borderTop: `1px solid ${theme.accent.main}`,
    borderRadius: 0,
    marginTop: theme.spacing(1),
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  cardLabel: {
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  cardAmount: {
    flexShrink: 0,
    textAlign: 'right',
    fontWeight: 500,
    color: theme.palette.primary.darker,
    fontVariantNumeric: 'tabular-nums',
  },
  strong: { fontWeight: 500 },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

type lineItemProps = {
  value: string | number;
  label: string;
  date: string;
  variant?: LineItemVariant;
  handleEdit?: () => void;
  handleDelete?: () => void;
  hideActions?: boolean;
  hideDate?: boolean;
  isTotal?: boolean;
};

const formatEntryDate = (date: string) => {
  if (!date) return '';
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : '';
};

const LineItem: FC<lineItemProps> = ({
  value,
  label,
  date,
  variant = 'row',
  handleEdit,
  handleDelete,
  hideActions = false,
  hideDate = false,
  isTotal = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const showActions = !hideActions && !!(handleEdit || handleDelete);
  const buttonClassName = clsx(classes.actionButton, { [classes.actionButtonTouch]: variant === 'card' });
  const iconSize = variant === 'card' ? 'sm' : 'xs';

  const actions = showActions && (
    <Box className={classes.actions}>
      {handleEdit && (
        <IconButton
          size="small"
          className={buttonClassName}
          aria-label={`${t('common:edit')} ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          <FontAwesomeIcon size={iconSize} icon="edit" />
        </IconButton>
      )}
      {handleDelete && (
        <IconButton
          size="small"
          className={buttonClassName}
          aria-label={`${t('common:delete')} ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
        >
          <FontAwesomeIcon size={iconSize} icon="trash" />
        </IconButton>
      )}
    </Box>
  );

  const labelNode = (
    <Box className={classes.labelWrapper}>
      <Tooltip title={label} arrow>
        <Typography className={classes.label}>{label}</Typography>
      </Tooltip>
    </Box>
  );

  const amountField = (
    <NumberField
      thousandSeparator
      prefix="$"
      decimalScale={2}
      disabled
      className={classes.numeric}
      value={value as number}
    />
  );

  const dateField = (
    <DatePicker disabled value={date} label={t('common:date')} onChange={() => { }} />
  );

  if (variant === 'card') {
    const formattedDate = formatEntryDate(date);
    return (
      <Box className={clsx(classes.card, { [classes.cardTotal]: isTotal })}>
        <Box className={classes.cardHeader}>
          <Typography variant="body2" className={clsx(classes.cardLabel, { [classes.strong]: isTotal })}>
            {label}
          </Typography>
          <Typography variant="body2" className={classes.cardAmount}>
            {formatMoney(Number(value) || 0)}
          </Typography>
        </Box>
        {(!hideDate || showActions) && (
          <Box className={classes.cardFooter}>
            <Typography variant="caption" color="textSecondary">
              {!hideDate && formattedDate ? `${t('common:date')}: ${formattedDate}` : ''}
            </Typography>
            {actions}
          </Box>
        )}
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Box className={classes.compact}>
        <Box className={classes.compactHeader}>
          {actions}
          {labelNode}
        </Box>
        <Box className={classes.compactFields}>
          <Box className={classes.compactField}>{amountField}</Box>
          {!hideDate && <Box className={classes.compactField}>{dateField}</Box>}
        </Box>
      </Box>
    );
  }

  return (
    <Grid item xs={12} className={classes.row}>
      <Grid container spacing={2}>
        <Grid item xs={4} className={classes.labelContainer}>
          {actions}
          {labelNode}
        </Grid>
        <Grid item xs={hideDate ? 8 : 3}>
          {amountField}
        </Grid>
        {!hideDate && (
          <Grid item xs={5}>
            {dateField}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default LineItem;
